import { MouseEventHandler, ReactNode } from "react";

import { Box, Button, Stack, SxProps, Theme } from "@mui/material";

interface Props {
  resetLabel: string;
  // Typed as a click handler, not `() => void`, so formik's handleReset (which formik itself types
  // inconsistently as taking a mandatory event) can be passed straight through.
  onReset: MouseEventHandler<HTMLButtonElement>;
  resetDisabled?: boolean;
  saveLabel: string;
  saveDisabled?: boolean;
  /**
   * "submit" (default) relies on an ancestor form's onSubmit, so onSave is not needed. Use "button"
   * for a bar with no surrounding form element, and pass onSave for the click handler.
   */
  saveType?: "submit" | "button";
  onSave?: MouseEventHandler<HTMLButtonElement>;
  /** Rendered between the reset and save buttons, e.g. a last-modified/change-history line. */
  info?: ReactNode;
  sx?: SxProps<Theme>;
}

// The reset-left / info+save-right action row shared by every saveable form in the app.
export function SaveBar({
  resetLabel,
  onReset,
  resetDisabled,
  saveLabel,
  saveDisabled,
  saveType = "submit",
  onSave,
  info,
  sx,
}: Props) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" p={0.5} sx={sx}>
      <Button variant="outlined" onClick={onReset} disabled={resetDisabled}>
        {resetLabel}
      </Button>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        {info}
        <Button
          variant="contained"
          type={saveType}
          onClick={saveType === "button" ? onSave : undefined}
          disabled={saveDisabled}
        >
          {saveLabel}
        </Button>
      </Stack>
    </Box>
  );
}
